import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { dataLayerPublish } from '@dhl/datalayer'
import { usePostProfileMobilePhone, useDeleteProfileMobilePhone } from '@dhl/receiver-profile-api'
import { Card, StackColumn, useSnackbar } from '@dhl-ui/components'

import { TrackTraceIllustration } from '@dhl-ui/illustrations'
import {
  type FormValues,
  initialValues,
  MobileNumberForm,
} from '~/profile/view/settings/mobileNumber/MobileNumberForm.js'
import { useWizardContext, useSetTitle } from '~/setup/model/context.js'
import { steps } from '~/setup/model/steps.js'
import { StepDescription } from '~/setup/view/components/StepDescription.js'
import { StepForm } from '~/setup/view/components/StepForm.js'

export const PhoneNumber: FC = () => {
  const [validationSchema, setValidationSchema] = useState<object | undefined>()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { onStepComplete, currentStep, setStepSubmittedValues, getStepSubmittedValues } = useWizardContext()

  useSetTitle(t('portal.profileSetup.phoneNumber.pageTitle'))
  const { mutateAsync: addProfileMobileNumber } = usePostProfileMobilePhone()
  const { mutateAsync: deleteProfileMobileNumber } = useDeleteProfileMobilePhone()

  const previouslySubmittedValues = getStepSubmittedValues<FormValues>(steps.phoneNumber)

  const handleSubmit = async (values: FormValues) => {
    try {
      const { isPreviouslyCompleted } = currentStep

      if (isPreviouslyCompleted) {
        const hasChanged = JSON.stringify(values) !== JSON.stringify(previouslySubmittedValues)

        if (!hasChanged) {
          onStepComplete()
          return
        }

        await deleteProfileMobileNumber()
      }

      await addProfileMobileNumber({
        data: values,
        params: {
          'x-lang': language,
        },
      })

      setStepSubmittedValues(steps.phoneNumber, values)
      onStepComplete()

      enqueueSnackbar(t('inputs.mobileNumberInput.mobileNumberVerificationCodeSent', { ...values }), {
        severity: 'info',
      })
    } catch (error) {
      dataLayerPublish('ERROR', { error, page: 'phone-number-wizard' })
      enqueueSnackbar(t('common.pleaseTryAgainErrorMessage'), { severity: 'error' })
      throw new Error('', { cause: error })
    }
  }

  return (
    <StackColumn gap={3}>
      <StepDescription
        slotProps={{ illustration: { variant: TrackTraceIllustration } }}
        title={t('portal.profileSetup.phoneNumber.stepTitle')}
        description={t('portal.profileSetup.phoneNumber.stepDescription')}
      />

      <StepForm
        initialValues={previouslySubmittedValues ?? initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Card>
          <Card.Header title={t('portal.profileSetup.phoneNumber.pageTitle')} />
          <Card.Content>
            <MobileNumberForm autoFocusInput={!currentStep.isOptional} onValidationSchemaChange={setValidationSchema} />
          </Card.Content>
        </Card>
      </StepForm>
    </StackColumn>
  )
}
